import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Video, Plus, Trash2, Edit, Play, Users, Package, CheckCircle, X, Filter } from "lucide-react";

const VideoManager = () => {
  const [videos, setVideos] = useState([
    {
      id: 1,
      title: "Como Conectar ao Mercado Livre",
      description: "Tutorial completo de integração com Mercado Livre",
      url: "https://www.youtube.com/watch?v=zqKVYB0rH-s",
      target: "afiliado",
      category: "integracao",
      duration: "8:45",
      views: 1250
    },
    {
      id: 2,
      title: "Como Cadastrar Produtos",
      description: "Passo a passo para cadastrar produtos no catálogo",
      url: "https://www.youtube.com/watch?v=EnTKOjd9P2s",
      target: "fornecedor",
      category: "produtos",
      duration: "12:30",
      views: 890
    },
    {
      id: 3,
      title: "Entendendo o Sistema de Comissões",
      description: "Como funcionam as comissões e pagamentos",
      url: "https://www.youtube.com/watch?v=WJc0rcWYJog",
      target: "afiliado",
      category: "comissoes",
      duration: "6:20",
      views: 2100
    },
    {
      id: 4,
      title: "Gerenciando Estoque",
      description: "Como controlar estoque e sincronizar com marketplaces",
      url: "https://www.youtube.com/watch?v=paRCord4bN0",
      target: "fornecedor",
      category: "estoque",
      duration: "10:15",
      views: 670
    },
    {
      id: 5,
      title: "Integração com Amazon",
      description: "Tutorial de integração com Amazon Seller Central",
      url: "https://www.youtube.com/shorts/cUWFY5zxLzs",
      target: "afiliado",
      category: "integracao",
      duration: "15:40",
      views: 1580
    },
    {
      id: 6,
      title: "Como usar a IA de Anúncios",
      description: "Crie anúncios profissionais automaticamente",
      url: "https://www.youtube.com/shorts/D_jEPpPnO90",
      target: "afiliado",
      category: "ia",
      duration: "9:00",
      views: 3200
    },
    {
      id: 7,
      title: "Configurando Fretes",
      description: "Como configurar tabelas de frete e envios",
      url: "https://www.youtube.com/watch?v=zqKVYB0rH-s",
      target: "fornecedor",
      category: "logistica",
      duration: "7:30",
      views: 980
    },
    {
      id: 8,
      title: "Dashboard de Vendas",
      description: "Entendendo métricas e relatórios",
      url: "https://www.youtube.com/watch?v=EnTKOjd9P2s",
      target: "afiliado",
      category: "analise",
      duration: "11:20",
      views: 1450
    }
  ]);

  const [showAddModal, setShowAddModal] = useState(false);
  const [filterTarget, setFilterTarget] = useState("todos");

  const [newVideo, setNewVideo] = useState({
    title: "",
    description: "",
    url: "",
    target: "afiliado",
    category: "",
    duration: ""
  });

  const filteredVideos = filterTarget === "todos" 
    ? videos 
    : videos.filter(v => v.target === filterTarget);

  const handleAddVideo = () => {
    if (!newVideo.title || !newVideo.url) {
      alert("Preencha título e URL do vídeo!");
      return;
    }

    const video = {
      id: videos.length + 1,
      ...newVideo,
      views: 0
    };

    setVideos([...videos, video]);
    setNewVideo({
      title: "",
      description: "",
      url: "",
      target: "afiliado",
      category: "",
      duration: ""
    });
    setShowAddModal(false);
    alert("✅ Vídeo adicionado com sucesso!");
  };

  const handleDeleteVideo = (id) => {
    if (window.confirm("Tem certeza que deseja deletar este vídeo?")) {
      setVideos(videos.filter(v => v.id !== id));
      alert("✅ Vídeo deletado!");
    }
  };

  const getYouTubeEmbedUrl = (url) => {
    const videoId = url.split("v=")[1]?.split("&")[0] || url.split("/").pop();
    return `https://www.youtube.com/embed/${videoId}`;
  };

  return (
    <div className="flex flex-col h-full bg-gray-950">
      {/* Header Fixo */}
      <div className="p-4 lg:p-6 border-b border-gray-800">
        <div className="flex items-center gap-2 mb-1">
          <span className="bg-gradient-to-r from-red-500 to-pink-500 text-white text-xs font-bold px-2 py-1 rounded-full uppercase">Admin</span>
          <h1 className="text-xl font-bold text-white">Gerenciador de Vídeos</h1>
        </div>
        <p className="text-sm text-gray-400">Organize e gerencie todos os tutoriais do sistema</p>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4">
          <Card className="bg-gradient-to-br from-blue-500 to-blue-600 p-3 border-none">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-white/80 text-xs mb-1">Total de Vídeos</p>
                <p className="text-white text-2xl font-bold">{videos.length}</p>
              </div>
              <Video className="w-6 h-6 text-white/80" />
            </div>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-purple-600 p-3 border-none">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-white/80 text-xs mb-1">Para Afiliados</p>
                <p className="text-white text-2xl font-bold">
                  {videos.filter(v => v.target === "afiliado").length}
                </p>
              </div>
              <Users className="w-6 h-6 text-white/80" />
            </div>
          </Card>

          <Card className="bg-gradient-to-br from-orange-500 to-orange-600 p-3 border-none">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-white/80 text-xs mb-1">Para Fornecedores</p>
                <p className="text-white text-2xl font-bold">
                  {videos.filter(v => v.target === "fornecedor").length}
                </p>
              </div>
              <Package className="w-6 h-6 text-white/80" />
            </div>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-green-600 p-3 border-none">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-white/80 text-xs mb-1">Total de Views</p>
                <p className="text-white text-2xl font-bold">
                  {videos.reduce((sum, v) => sum + v.views, 0).toLocaleString('pt-BR')}
                </p>
              </div>
              <Play className="w-6 h-6 text-white/80" />
            </div>
          </Card>
        </div>

        {/* Actions */}
        <div className="mt-4 flex items-center justify-between flex-wrap gap-2">
          <div className="flex gap-2">
            <Button
              onClick={() => setFilterTarget("todos")}
              size="sm"
              className={`h-8 ${filterTarget === "todos" ? "bg-orange-500 hover:bg-orange-600" : "bg-gray-700 hover:bg-gray-600"}`}
            >
              Todos
            </Button>
            <Button
              onClick={() => setFilterTarget("afiliado")}
              size="sm"
              className={`h-8 ${filterTarget === "afiliado" ? "bg-purple-500 hover:bg-purple-600" : "bg-gray-700 hover:bg-gray-600"}`}
            >
              <Users className="w-3 h-3 mr-1" />
              Afiliados
            </Button>
            <Button
              onClick={() => setFilterTarget("fornecedor")}
              size="sm"
              className={`h-8 ${filterTarget === "fornecedor" ? "bg-blue-500 hover:bg-blue-600" : "bg-gray-700 hover:bg-gray-600"}`}
            >
              <Package className="w-3 h-3 mr-1" />
              Fornecedores
            </Button>
          </div>

          <Button
            onClick={() => setShowAddModal(true)}
            className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 h-8"
          >
            <Plus className="w-4 h-4 mr-1" />
            Adicionar Vídeo
          </Button>
        </div>
      </div>

      {/* Conteúdo com Scroll */}
      <div className="flex-1 p-4 lg:p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pb-4">
          {filteredVideos.map((video) => (
            <Card key={video.id} className="bg-gray-800/50 border-gray-700 overflow-hidden hover:border-gray-600 transition-all">
              {/* Thumbnail */}
              <div className="relative h-40 bg-gray-900">
                <iframe
                  src={getYouTubeEmbedUrl(video.url)}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>

              {/* Content */}
              <div className="p-3">
                {/* Badge */}
                <div className="mb-2">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
                    video.target === "afiliado"
                      ? "bg-purple-500/20 text-purple-400"
                      : "bg-blue-500/20 text-blue-400"
                  }`}>
                    {video.target === "afiliado" ? <Users className="w-3 h-3" /> : <Package className="w-3 h-3" />}
                    {video.target === "afiliado" ? "Afiliado" : "Fornecedor"}
                  </span>
                </div>

                {/* Title */}
                <h3 className="text-base font-bold text-white mb-1 line-clamp-1">
                  {video.title}
                </h3>

                {/* Description */}
                <p className="text-gray-400 text-xs mb-3 line-clamp-2">
                  {video.description}
                </p>

                {/* Stats */}
                <div className="flex items-center justify-between text-xs text-gray-500 mb-3">
                  <span>⏱️ {video.duration}</span>
                  <span>👁️ {video.views.toLocaleString('pt-BR')} views</span>
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 border-gray-700 h-7 text-xs"
                    onClick={() => window.open(video.url, '_blank')}
                  >
                    <Play className="w-3 h-3 mr-1" />
                    Assistir
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="border-red-700 text-red-400 hover:bg-red-500/10 h-7 w-7 p-0"
                    onClick={() => handleDeleteVideo(video.id)}
                  >
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Add Video Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center overflow-y-auto py-8 z-50 p-4">
          <Card className="bg-gray-900 border-gray-700 max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-white">Adicionar Novo Vídeo</h2>
                <Button variant="ghost" size="sm" onClick={() => setShowAddModal(false)} className="h-8 w-8 p-0">
                  <X className="w-4 h-4" />
                </Button>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">Título do Vídeo *</label>
                  <Input
                    value={newVideo.title}
                    onChange={(e) => setNewVideo({...newVideo, title: e.target.value})}
                    placeholder="Ex: Como Integrar com Mercado Livre"
                    className="bg-gray-800 border-gray-700 text-white h-9"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">Descrição</label>
                  <textarea
                    value={newVideo.description}
                    onChange={(e) => setNewVideo({...newVideo, description: e.target.value})}
                    placeholder="Descrição breve do vídeo"
                    rows={2}
                    className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">URL do YouTube *</label>
                  <Input
                    value={newVideo.url}
                    onChange={(e) => setNewVideo({...newVideo, url: e.target.value})}
                    placeholder="https://www.youtube.com/watch?v=..."
                    className="bg-gray-800 border-gray-700 text-white h-9"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Para quem? *</label>
                    <select
                      value={newVideo.target}
                      onChange={(e) => setNewVideo({...newVideo, target: e.target.value})}
                      className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-3 py-2 h-9 text-sm"
                    >
                      <option value="afiliado">Afiliado</option>
                      <option value="fornecedor">Fornecedor</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-300 mb-1">Duração</label>
                    <Input
                      value={newVideo.duration}
                      onChange={(e) => setNewVideo({...newVideo, duration: e.target.value})}
                      placeholder="Ex: 10:30"
                      className="bg-gray-800 border-gray-700 text-white h-9"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-300 mb-1">Categoria</label>
                  <Input
                    value={newVideo.category}
                    onChange={(e) => setNewVideo({...newVideo, category: e.target.value})}
                    placeholder="Ex: integracao, produtos, comissoes"
                    className="bg-gray-800 border-gray-700 text-white h-9"
                  />
                </div>
              </div>

              <div className="flex gap-2 mt-4">
                <Button
                  onClick={() => setShowAddModal(false)}
                  variant="outline"
                  className="flex-1 border-gray-700 h-9"
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleAddVideo}
                  className="flex-1 bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 h-9"
                >
                  <CheckCircle className="w-4 h-4 mr-1" />
                  Adicionar
                </Button>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default VideoManager;
